import { ReactNode, useEffect, useState } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";
import { useAnalyticsClient } from "src/analytics/wac-analytics/analytics-client";
import { Adg4Button } from "src/components/adg4/button/src/button";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";
import Tooltip from "@atlaskit/tooltip";

import { useUserContext } from "@/components/user-context";
import { Picture } from "@/components/wpl/image/src";
import { Link } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";
import { useLocalizedHref } from "@/libraries/web-framework";
import { JiraMarkBrand } from "@/libraries/wpl/foundations/src/logos/jira-mark-brand";
import { AvatarFacepile } from "@/patterns/return-to-product-modal/src/avatar-facepile";
import {
    filterAvailableJiraSites,
    useRecentJiraSite
} from "@/templates/software/jira/logged-in/utils/use-recent-jira-site";
import { useLoggedInUser } from "@/utils/logged-in-user-provider";

import { JiraSiteContainer } from "./jira-site-container";
import { useJiraSitesContext } from "./jira-sites-provider/jira-sites-provider";
import { getAtlasTicket } from "./utils/get-atlas-ticket";

enum TRACKING_EVENT_NAMES {
    loggedInHeroProductCtaClicked = "loggedInHeroProductCtaClicked",
    loggedInHeroAnotherSiteCtaClicked = "loggedInHeroAnotherSiteCtaClicked",
    loggedInHeroExploreFeaturesClicked = "loggedInHeroExploreFeaturesClicked",
    loggedInHeroCreateNewSiteClicked = "loggedInHeroCreateNewSiteClicked"
}

const START_URL =
    process.env["NODE_ENV"] === "production"
        ? "https://start.atlassian.com"
        : "https://start.stg.atlassian.com";

export const loggedInHeroAnalyticsPayload = {
    eventType: "ui",
    action: "clicked",
    actionSubject: "button",
    actionSubjectId: "loggedInHero",
    source: "/software/jira"
};

const jswLoggedInCss = css({
    position: "relative",
    background: wacToken("color.extended.blue100"),
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    display: "grid",
    gridTemplateColumns: "1fr",
    placeItems: "center",
    paddingInline: "100px",
    paddingBlockStart: "25px",
    paddingBlockEnd: "50px",
    width: "100%",
    [media.below.md]: {
        paddingInline: "25px"
    }
});

const heroSectionCss = css({
    position: "relative",
    color: wacToken("color.primary.black"),
    width: "100%",
    maxWidth: "928px",
    padding: "20px",
    [media.below.sm]: {
        paddingInline: "0px"
    }
});

const anchorContainer = css({
    position: "relative",
    flexGrow: "1",
    alignSelf: "stretch"
});

const blueBackdrop = css({
    position: "absolute",
    insetBlockStart: "20%",
    insetInlineEnd: "-20px",
    width: "85%",
    height: "100%",
    backgroundColor: wacToken("color.extended.blue700")
});

const jiraSiteBoxCss = css({
    backgroundColor: wacToken("color.neutral.light0"),
    borderRadius: "4px",
    width: "100%",
    minWidth: "300px",
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "row",
    alignItems: "center",
    border: `1px solid ${wacToken("color.primary.black")}`,
    paddingBlock: "16px",
    paddingInline: "16px",
    marginBlock: "20px",
    position: "relative"
});

const headingCss = css({
    fontSize: "48px",
    lineHeight: "56px",
    fontWeight: "800",
    fontFamily: wacToken("font.charlie.display"),
    marginBlockEnd: "15px",
    paddingInlineStart: "0",
    [media.below.md]: {
        fontSize: "35px",
        lineHeight: "40px"
    }
});

const starCss = css({
    position: "absolute",
    display: "inline-block",
    insetBlockEnd: "100px",
    insetInlineStart: "-50px",
    transform: "rotate(10deg)",
    [media.below.md]: {
        display: "none"
    }
});

const treatmentStarCss = css({
    transform: "rotate(10deg) scale(-1, 1)",
    position: "absolute",
    display: "inline-block",
    insetBlockEnd: "150px",
    insetInlineStart: "892px",
    [media.below.md]: {
        display: "none"
    }
});

const differentSiteCss = css({
    fontFamily: wacToken("font.charlie.text"),
    marginBlockStart: "15px",
    fontSize: "14px",
    fontWeight: "400",
    [media.below.md]: {
        textAlign: "center"
    }
});

const subHeadingContainerCss = css({
    display: "flex",
    flexWrap: "wrap",
    columnGap: "100px",
    justifyContent: "space-between",
    alignItems: "baseline"
});

const createNewSiteAnchorCss = css({
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "16px",
    fontWeight: "600",
    lineHeight: "28px",
    color: wacToken("color.extended.blue700")
});

const subHeadingCss = css({
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "20px",
    lineHeight: "28px",
    fontWeight: "400"
});

const exploreFeaturesContainerCss = css({
    marginBlockStart: "64px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center"
});

const exploreFeaturesHeadingCss = css({
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "20px",
    lineHeight: "28px",
    fontWeight: "400",
    marginBlockEnd: "16px"
});

const exploreFeaturesCtaCss = css({
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "16px",
    fontWeight: "600",
    background: "none",
    appearance: "none",
    borderRadius: "30px",
    padding: "10px 30px",
    border: "2px solid",
    cursor: "pointer"
});

const nameUnderlineCss = css({
    fontStyle: "normal",
    position: "relative",
    [":after"]: {
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='69' height='13' viewBox='0 0 69 13' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg id='Gestural Line/star 5'%3E%3Cpath id='Vector' d='M68.9943 6.17916C57.1037 8.38955 48.3305 2.73368 36.3215 7.37322C35.7609 5.38615 34.3116 2.89774 32.1422 2.86128C33.9516 2.90685 36.8456 8.09785 33.9379 4.62047C32.2288 1.97255 24.7591 5.20844 30.4833 2.9069C26.5319 3.39455 22.9087 6.61668 20.238 9.48335C15.7944 13.5669 19.167 5.66417 15.954 1.69002C11.401 -3.83369 -2.41738 10.5316 0.367259 10.5407C2.14013 8.74051 4.51915 7.61935 5.50357 5.62316C22.8358 -6.19447 10.33 15.677 20.6528 12.0128C37.5155 -3.17739 28.993 10.5726 38.8919 9.63373C44.6617 4.12826 69.1948 12.7602 68.9988 6.17916H68.9943Z' fill='%23FCA700'/%3E%3C/g%3E%3C/svg%3E%0A")`,
        backgroundRepeat: "no-repeat",
        color: wacToken("color.extended.yellow400"),
        backgroundSize: "contain",
        content: "''",
        height: "20px",
        insetBlockEnd: -14,
        insetInlineEnd: -2,
        insetInlineStart: -4,
        position: "absolute",
        [media.only.md]: {
            insetBlockEnd: -10
        },
        [media.above.lg]: {
            insetBlockEnd: -6
        }
    }
});

const inlineJiraLogoCss = css({
    display: "inline-block",
    transform: "scale(0.5)",
    position: "relative",
    insetBlockStart: "14px",
    marginInline: "-10px"
});

const siteInfoContainerCss = css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    height: "64px",
    gap: token("space.200")
});

const sitenameIconCss = css({
    background: wacToken("color.extended.lime300"),
    borderRadius: token("space.050"),
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "64px",
    width: "64px",
    minWidth: "64px"
});

const sitenameIconTextCss = css({
    color: wacToken("color.extended.lime800"),
    fontFamily: wacToken("font.charlie.display"),
    fontSize: token("space.300"),
    fontWeight: 600,
    lineHeight: "28px",
    letterSpacing: "0.3px",
    textAlign: "center"
});

const sitenameAndAvatarContainerCss = css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: "10px",
    position: "relative",
    height: "64px"
});

const siteNameSpanCss = css({
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "20px",
    fontWeight: "700",
    height: "20px",
    lineHeight: "1rem",
    textAlign: "left",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    overflow: "hidden",
    display: "inline-block",
    verticalAlign: "top",
    [media.only.xs]: {
        maxWidth: "200px"
    },
    [media.below.xs]: {
        maxWidth: "90px"
    }
});

const tooltipWrapperCss = css({
    height: "20px"
});

const avatarContainerCss = css({
    height: "24px",
    [media.only.xs]: {
        maxWidth: "200px"
    },
    [media.below.xs]: {
        maxWidth: "90px"
    }
});

// eslint-disable-next-line complexity
export const LoggedInHeroSection = ({
    experimentValue
}: {
    experimentValue: string;
}) => {
    const loggedInUserProfile = useLoggedInUser();
    const { user, profile } = useUserContext();
    const { jiraSitesFromAgg } = useJiraSitesContext();

    const [teammateAvatars, setTeammateAvatars] = useState<string[]>([]);

    const isTreatment = experimentValue === "jfind-1506-after-d7-gubtp-organic";

    const intl = useIntl();

    // Try to get name from props, and fallback to hook if needed
    const userFirstName =
        user.name?.split(" ")[0] ?? loggedInUserProfile.name?.split(" ")[0];

    // Try to get picture from props, and fallback to hook if needed
    const userPicture = user?.picture ?? loggedInUserProfile.picture;
    const avatars =
        jiraSitesFromAgg?.jira?.allJiraProjects.edges[0]?.node.assignableUsers
            .edges;

    useEffect(() => {
        setTeammateAvatars(
            jiraSitesFromAgg?.jira?.allJiraProjects.edges[0]?.node.assignableUsers.edges
                .filter(
                    (assignableUser) =>
                        assignableUser.node.picture != userPicture &&
                        assignableUser.node.accountStatus === "active"
                )
                .map((assignableUser) => {
                    return assignableUser.node.picture ?? "";
                }) ?? []
        );
    }, [avatars, jiraSitesFromAgg?.jira?.allJiraProjects.edges, userPicture]);

    const { createAnalyticsEvent } = useAnalyticsEvents();
    const analyticsClient = useAnalyticsClient();

    const localizedTryLink = useLocalizedHref(
        "/try/cloud/signup?bundle=jira-software&createNew=true"
    );
    const localizedProductGuideLink = useLocalizedHref(
        "/software/jira/guides/getting-started/introduction"
    );

    const [hasMultipleJiras, setHasMultipleJiras] = useState(false);
    const [siteName, setSiteName] = useState("");
    const [siteInitials, setSiteInitials] = useState("");

    useEffect(() => {
        if (isTreatment) {
            return;
        }
        const availableJiraSites = filterAvailableJiraSites(
            profile.attributes?.existingSites
        );
        // Default state to whatever we might have got from SSR
        setHasMultipleJiras(availableJiraSites.length > 1);

        const siteNameFromAvailableJiraSites =
            availableJiraSites[0]?.displayName || "";
        setSiteName(siteNameFromAvailableJiraSites);
        setSiteInitials(
            siteNameFromAvailableJiraSites.slice(0, 1).toUpperCase() +
                siteNameFromAvailableJiraSites.slice(1, 2)
        );
    }, [profile.attributes?.existingSites, isTreatment]);

    const [siteUrl, setSiteUrl] = useState("");

    useEffect(() => {
        if (isTreatment) {
            return;
        }
        if (!profile && !jiraSitesFromAgg) {
            return;
        }
        const siteFromUserProfile = profile?.attributes?.existingSites[0];
        let projectUrl = siteFromUserProfile?.url;
        if (jiraSitesFromAgg?.jira?.allJiraProjects.edges.length === 1) {
            projectUrl =
                jiraSitesFromAgg.jira.allJiraProjects?.edges[0]?.node.webUrl;
        }
        setSiteUrl(projectUrl ?? "");
    }, [
        jiraSitesFromAgg,
        jiraSitesFromAgg?.jira?.allJiraProjects.edges,
        profile,
        profile?.attributes?.existingSites,
        isTreatment
    ]);

    const {
        hasMultipleJiras: mostRecentHasMultipleJiras,
        siteName: mostRecentSiteName,
        siteUrl: mostRecentSiteUrl,
        siteInitials: mostRecentSiteInitials
    } = useRecentJiraSite();

    useEffect(() => {
        if (!isTreatment) {
            return;
        }
        setHasMultipleJiras(mostRecentHasMultipleJiras);
        setSiteName(mostRecentSiteName);
        setSiteUrl(mostRecentSiteUrl);
        setSiteInitials(mostRecentSiteInitials);
    }, [
        isTreatment,
        mostRecentHasMultipleJiras,
        mostRecentSiteInitials,
        mostRecentSiteName,
        mostRecentSiteUrl
    ]);

    const loggedInHeroLoadedAnalyticsPayload = {
        eventType: "ui",
        action: "loaded",
        actionSubject: "component",
        actionSubjectId: "loggedInHero",
        source: "/software/jira",
        attributes: {
            atlasId: getAtlasTicket(experimentValue)
        }
    };

    useEffect(() => {
        //if client side, fire analytics event
        if (typeof window !== "undefined") {
            createAnalyticsEvent({
                ...loggedInHeroLoadedAnalyticsPayload
            }).fire();
        }
    }, [analyticsClient, createAnalyticsEvent]);

    const clickHandler = (eventName: TRACKING_EVENT_NAMES) => {
        createAnalyticsEvent({
            ...loggedInHeroAnalyticsPayload,
            attributes: {
                eventName,
                url: window.location.href,
                atlasId: getAtlasTicket(experimentValue)
            }
        }).fire();
    };

    return (
        <section css={jswLoggedInCss}>
            <div css={heroSectionCss}>
                {isTreatment ? (
                    <div css={treatmentStarCss}>
                        <Picture
                            image={{
                                width: 88,
                                height: 70,
                                src: "https://dam-cdn.atl.orangelogic.com/AssetLink/g06k378gucflnfse7s88i0365h14j40v.webp",
                                alt: "hand-drawn stars"
                            }}
                        />
                    </div>
                ) : (
                    <div css={starCss}>
                        <Picture
                            image={{
                                width: 88,
                                height: 70,
                                src: "https://dam-cdn.atl.orangelogic.com/AssetLink/g06k378gucflnfse7s88i0365h14j40v.webp",
                                alt: "hand-drawn stars"
                            }}
                        />
                    </div>
                )}
                <h1 css={headingCss}>
                    {userFirstName ? (
                        <FormattedMessage
                            id="JiraSoftwarePageTemplate.loggedInHero.welcomeBackWithName"
                            defaultMessage={`Welcome back, {name}.`}
                            values={{
                                name: (
                                    <span css={nameUnderlineCss}>
                                        {userFirstName}
                                    </span>
                                )
                            }}
                        />
                    ) : (
                        <FormattedMessage
                            id="JiraSoftwarePageTemplate.loggedInHero.welcomeBack"
                            defaultMessage="Welcome back"
                        />
                    )}
                </h1>
                <div css={subHeadingContainerCss}>
                    <div css={subHeadingCss}>
                        <FormattedMessage
                            id="JiraSoftwarePageTemplate.loggedInHero.pickUpWhereYouLeftOffInProduct"
                            defaultMessage="Pick up where you left off in <span>{product}</span>"
                            values={{
                                product: "Jira",
                                span: (text: ReactNode) => (
                                    <span>
                                        <div css={inlineJiraLogoCss}>
                                            <JiraMarkBrand />
                                        </div>
                                        {text}
                                    </span>
                                )
                            }}
                        />
                    </div>
                    <a
                        data-testid="createNewSiteAnchor"
                        css={createNewSiteAnchorCss}
                        href={localizedTryLink}
                        onClick={() =>
                            clickHandler(
                                TRACKING_EVENT_NAMES.loggedInHeroCreateNewSiteClicked
                            )
                        }
                    >
                        <FormattedMessage
                            id="WacSignupForm.createNewSite"
                            defaultMessage="Create a new site"
                        />
                    </a>
                </div>
                <div css={anchorContainer}>
                    {!isTreatment && <div css={blueBackdrop} />}
                    <div css={jiraSiteBoxCss}>
                        <JiraSiteContainer isTreatment={isTreatment}>
                            <div css={siteInfoContainerCss}>
                                <div css={sitenameIconCss}>
                                    <h4 css={sitenameIconTextCss}>
                                        {siteInitials}
                                    </h4>
                                </div>
                                <div css={sitenameAndAvatarContainerCss}>
                                    <div css={tooltipWrapperCss}>
                                        <Tooltip
                                            content={siteName}
                                            position={"top-end"}
                                            mousePosition="top"
                                            tag={"span"}
                                        >
                                            <span css={siteNameSpanCss}>
                                                {siteName}
                                            </span>
                                        </Tooltip>
                                    </div>
                                    <div css={avatarContainerCss}>
                                        {userPicture && (
                                            <AvatarFacepile
                                                max={4}
                                                iconSize={24}
                                                icons={[
                                                    userPicture,
                                                    ...teammateAvatars
                                                ]}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                            <Adg4Button
                                data-testid="goToJiraButton"
                                variant="primary"
                                color={"yellow"}
                                href={siteUrl}
                                size="small"
                                onClick={() =>
                                    clickHandler(
                                        TRACKING_EVENT_NAMES.loggedInHeroProductCtaClicked
                                    )
                                }
                            >
                                <FormattedMessage
                                    id="JiraSoftwarePageTemplate.loggedInHero.goToJira"
                                    defaultMessage="Go to Jira"
                                />
                            </Adg4Button>
                        </JiraSiteContainer>
                    </div>
                </div>
                {hasMultipleJiras && (
                    <div css={differentSiteCss}>
                        <Link
                            text={intl.formatMessage({
                                id: "ConfluenceSoftwarePageTemplate.loggedInHero.lookingForDifferentSite",
                                defaultMessage: "Looking for a different site?"
                            })}
                            href={START_URL}
                            type="primary"
                            onClick={() =>
                                clickHandler(
                                    TRACKING_EVENT_NAMES.loggedInHeroAnotherSiteCtaClicked
                                )
                            }
                        />
                    </div>
                )}
                <div css={exploreFeaturesContainerCss}>
                    <div css={exploreFeaturesHeadingCss}>
                        <FormattedMessage
                            id="JiraSoftwarePageTemplate.loggedInHero.findOutMore"
                            defaultMessage="Want to find out more about Jira?"
                        />
                    </div>
                    <a
                        data-testid="exploreFeaturesAnchor"
                        href={localizedProductGuideLink}
                        onClick={() =>
                            clickHandler(
                                TRACKING_EVENT_NAMES.loggedInHeroExploreFeaturesClicked
                            )
                        }
                    >
                        <button css={exploreFeaturesCtaCss}>
                            <FormattedMessage
                                id="jira-product-tour.staggered-double-feature-section.first-feature-link-text"
                                defaultMessage="Explore features"
                            />
                        </button>
                    </a>
                </div>
            </div>
        </section>
    );
};

LoggedInHeroSection.displayName = "LoggedInHeroSection";

export default LoggedInHeroSection;
